import { useCallback, useEffect, useMemo } from 'react'

import { useFragment } from '@apollo/client'
import { Dialog } from '@factorialco/f0-react/dist/experimental'
import {
  DiscardOutdatedTaskDocument,
  InboxTaskListItemFragmentDoc,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'

import { trackDiscardInboxTask, trackGoToDiscardInboxTask } from 'modules/inbox_tasks/lib/events'
import {
  getTaskListItemAffecteeInfo,
  getTaskTitle,
  isTaskFlaggedToBeDiscarded,
} from 'modules/inbox_tasks/lib/helpers'

type Data = {
  id: string
}

export const DiscardTaskActivity: ActivityComponent<Data> = ({ id }) => {
  const { open, closeActivity } = useActivity()

  const [discardTask] = useMutation(DiscardOutdatedTaskDocument, {
    extractorFn: (data) => data.tasksMutations.discardOutdatedTasksInboxTask,
  })

  const { data } = useFragment({
    fragment: InboxTaskListItemFragmentDoc,
    fragmentName: 'InboxTaskListItem',
    from: {
      __typename: 'TasksInboxTask',
      id,
    },
  })

  const flaggedToBeDiscarded = isTaskFlaggedToBeDiscarded(data)

  const taskCategory = data.category ?? undefined

  useEffect(() => {
    trackGoToDiscardInboxTask({ category: taskCategory })
  }, [taskCategory])

  useEffect(() => {
    if (!flaggedToBeDiscarded) {
      closeActivity()
    }
  }, [flaggedToBeDiscarded, closeActivity])

  const taskTitle = useMemo(() => {
    if (!data) return

    const groupedCount = data?.groupedTasksConnection?.totalCount ?? 0
    const { fullName: affecteeName } = getTaskListItemAffecteeInfo(data)

    const title = getTaskTitle({
      taskName: data?.name ?? '',
      taskableInfoForTitle: data.taskable,
      affecteeName,
      groupedCount,
      dueOn: data.dueOn ?? undefined,
    })

    return title
  }, [data])

  const description = useMemo(
    () =>
      `${
        taskTitle
          ? i18n.t('tasks.discard_task.description_first_with_task_title', { title: taskTitle })
          : i18n.t('tasks.discard_task.description_first_no_task_title')
      } ${i18n.t('tasks.discard_task.description_second')}`,
    [taskTitle]
  )

  const handleDiscard = useCallback(() => {
    trackDiscardInboxTask({
      category: taskCategory,
    })
    discardTask({ variables: { taskId: String(id) } }).then(closeActivity)
  }, [taskCategory, discardTask, id, closeActivity])

  const actions = useMemo(
    () => ({
      primary: {
        label: i18n.t('tasks.discard_task.discard'),
        variant: 'critical' as const,
        onClick: handleDiscard,
      },
      secondary: {
        label: i18n.t('common.cancel'),
        onClick: closeActivity,
      },
    }),
    [handleDiscard, closeActivity]
  )

  const header = useMemo(
    () => ({
      title: i18n.t('tasks.discard_task.title'),
      description,
      type: 'critical',
    }),
    [description]
  )

  return <Dialog actions={actions} header={header} onClose={closeActivity} open={open} />
}
